import { useCallback, useEffect, useState, useSyncExternalStore } from "react"

import { logger } from "@/lib/logger"

const log = logger("PylonChat")

const BILLING_PREFILL_MESSAGE = "I need help with billing"
const TICKET_FORM_SLUG = "billing-issues"
const KNOWLEDGE_BASE_ARTICLE_ID = "billing-overview"

type PylonCallback<TArgs extends unknown[] = []> = (...args: TArgs) => void

type PylonCommandMap =
  | { command: "show"; args: [] }
  | { command: "showNewMessage"; args: [string] }
  | { command: "showTicketForm"; args: [string] }
  | { command: "showKnowledgeBaseArticle"; args: [string] }
  | { command: "hide"; args: [] }
  | { command: "hideChatBubble"; args: [] }
  | { command: "showChatBubble"; args: [] }
  | {
      command: "onChangeUnreadMessagesCount"
      args: [PylonCallback<[number]> | null]
    }
  | { command: "onShow"; args: [PylonCallback | null] }
  | { command: "onHide"; args: [PylonCallback | null] }
  | { command: "setNewIssueCustomFields"; args: [Record<string, unknown>] }
  | { command: "setTicketFormFields"; args: [Record<string, unknown>] }

type ExtractPylonArgs<TCommand extends PylonCommandMap["command"]> = Extract<
  PylonCommandMap,
  { command: TCommand }
>["args"]

type ExtractPylonReturn<TCommand extends PylonCommandMap["command"]> =
  Extract<PylonCommandMap, { command: TCommand }> extends { return: infer R }
    ? R
    : undefined

type PylonApi = <TCommand extends PylonCommandMap["command"]>(
  command: TCommand,
  ...args: ExtractPylonArgs<TCommand>
) => ExtractPylonReturn<TCommand>

type PublicPylonCommand = Extract<
  PylonCommandMap,
  {
    command:
      | "show"
      | "showNewMessage"
      | "showTicketForm"
      | "showKnowledgeBaseArticle"
  }
>

declare global {
  interface Window {
    Pylon?: PylonApi
  }
}

const getPylonApi = (): PylonApi | undefined => {
  if (typeof window === "undefined") {
    return undefined
  }

  return window.Pylon
}

const usePylonReady = () => {
  const [isReady, setIsReady] = useState(() => Boolean(getPylonApi()))

  useEffect(() => {
    if (isReady) {
      return
    }

    const intervalId = window.setInterval(() => {
      if (getPylonApi()) {
        setIsReady(true)
      }
    }, 300)

    return () => {
      clearInterval(intervalId)
    }
  }, [isReady])

  return isReady
}

// External store for unread messages count
const createUnreadCountStore = () => {
  let unreadCount = 0
  const listeners = new Set<() => void>()
  let pylonListener: PylonCallback<[number]> | null = null
  let isSubscribed = false

  const notify = () => {
    listeners.forEach((listener) => listener())
  }

  const setupPylonSubscription = () => {
    if (isSubscribed) {
      return
    }

    const api = getPylonApi()
    if (!api) {
      return
    }

    try {
      pylonListener = (count: number) => {
        unreadCount = count
        notify()
      }
      api("onChangeUnreadMessagesCount", pylonListener)
      isSubscribed = true
    } catch (error) {
      log.error("Failed to initialize unread count listener", error)
    }
  }

  const teardownPylonSubscription = () => {
    if (!isSubscribed) {
      return
    }

    const api = getPylonApi()
    if (api && pylonListener) {
      try {
        api("onChangeUnreadMessagesCount", null)
        isSubscribed = false
        pylonListener = null
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          log.warn("Failed to remove unread listener during cleanup", {
            error,
          })
        }
      }
    }
  }

  const subscribe = (onStoreChange: () => void) => {
    listeners.add(onStoreChange)

    // Set up Pylon subscription if API is ready
    setupPylonSubscription()

    return () => {
      listeners.delete(onStoreChange)

      // Clean up Pylon subscription when no more React listeners
      if (listeners.size === 0) {
        teardownPylonSubscription()
      }
    }
  }

  const getSnapshot = () => unreadCount

  const getServerSnapshot = () => 0

  // Expose method to re-initialize when Pylon becomes ready
  const ensureSubscription = () => {
    if (listeners.size > 0) {
      setupPylonSubscription()
    }
  }

  return { subscribe, getSnapshot, getServerSnapshot, ensureSubscription }
}

const unreadCountStore = createUnreadCountStore()

function dispatchPublicPylonCommand(
  api: PylonApi,
  invocation: PublicPylonCommand,
) {
  switch (invocation.command) {
    case "show":
      api("show")
      break
    case "showNewMessage":
      api("showNewMessage", invocation.args[0])
      break
    case "showTicketForm":
      api("showTicketForm", invocation.args[0])
      break
    case "showKnowledgeBaseArticle":
      api("showKnowledgeBaseArticle", invocation.args[0])
      break
  }
}

export const usePylonChat = () => {
  const isPylonReady = usePylonReady()

  const invokePylon = useCallback((invocation: PublicPylonCommand) => {
    const api = getPylonApi()
    const { command } = invocation

    if (!api) {
      if (process.env.NODE_ENV !== "production") {
        log.warn(
          `Attempted to call command "${command}" before widget was ready`,
        )
      }
      return
    }

    try {
      dispatchPublicPylonCommand(api, invocation)
    } catch (error) {
      log.error(`Failed to execute command "${command}"`, error)
    }
  }, [])

  // Use useSyncExternalStore for unread count subscription
  const unreadCount = useSyncExternalStore(
    unreadCountStore.subscribe,
    unreadCountStore.getSnapshot,
    unreadCountStore.getServerSnapshot,
  )

  // Initialize Pylon chat bubble and ensure subscription when ready
  useEffect(() => {
    if (!isPylonReady) {
      return
    }

    const api = getPylonApi()
    if (!api) {
      return
    }

    try {
      api("hideChatBubble")
      // Ensure the store subscription is set up now that Pylon is ready
      unreadCountStore.ensureSubscription()
    } catch (error) {
      log.error("Failed to initialize chat listeners", error)
    }
  }, [isPylonReady])

  const handleOpenChat = useCallback(() => {
    invokePylon({ command: "show", args: [] })
  }, [invokePylon])

  const handlePrefillBilling = useCallback(() => {
    invokePylon({
      command: "showNewMessage",
      args: [BILLING_PREFILL_MESSAGE],
    })
  }, [invokePylon])

  const handleReportIssue = useCallback(() => {
    invokePylon({ command: "showTicketForm", args: [TICKET_FORM_SLUG] })
  }, [invokePylon])

  const handleOpenKnowledgeBase = useCallback(() => {
    invokePylon({
      command: "showKnowledgeBaseArticle",
      args: [KNOWLEDGE_BASE_ARTICLE_ID],
    })
  }, [invokePylon])

  return {
    unreadCount,
    handleOpenChat,
    handlePrefillBilling,
    handleReportIssue,
    handleOpenKnowledgeBase,
    billingPrefillMessage: BILLING_PREFILL_MESSAGE,
    ticketFormSlug: TICKET_FORM_SLUG,
    knowledgeBaseArticleId: KNOWLEDGE_BASE_ARTICLE_ID,
  }
}
